// HeroForge's class choice (docs/59): a Fighter's feat, a Rogue's specialty, a Cleric's patron god,
// a Ranger's named animal companion or a Wizard's three spells. The choice itself lives in
// HeroForge (it feeds `previewHero` and `forge_hero_called`); this draws its slot (docs/80): the
// pick in one row, the options only while the slot is open.
import type { ReactNode } from 'react';
import { Badge, Card, Field, HeroSprite, Input, Segmented } from '../../components';
import type { WasmAbility } from '../../../core/sim/actions';
import { coatFrame } from '../../../core/sim/companion';
import type { Callings } from '../../../core/sim/WasmSimBridge';
import { CALLING_HEAD, CALLING_KIND, callingDetailsKind, callingsFor, companionLine, costWord, rangeWord, slotWord, type CallingChoice, type DetailsKind } from '../forge';
import { DetailsButton } from './Details';
import { Slot, type SlotKey } from './Slot';

export interface CallingProps {
  cls: number;
  callings: Callings;
  catalogue: readonly WasmAbility[];
  choice: CallingChoice;
  onChange: (c: CallingChoice) => void;
  onToggleSpell: (id: number) => void;
  onDetails: (kind: DetailsKind, id: number) => void;
  resource: string;
  open: boolean;
  onToggle: (slot: SlotKey) => void;
  /** A single pick was made: the slot may close. */
  onPicked: () => void;
}

/** A choice card with its details button beside (not inside) the card's own button. */
export function Pick({ children, details }: { children: ReactNode; details: ReactNode }) {
  return <div className="fk-forge__pick">{children}{details}</div>;
}

const slotTone = (slot: WasmAbility['slot']) => (slot === 'Bonus' ? 'plum' : slot === 'Passive' ? 'grey' : 'blue') as 'plum' | 'grey' | 'blue';

export function CallingSlot({ cls, callings, catalogue, choice, onChange, onToggleSpell, onDetails, resource, open, onToggle, onPicked }: CallingProps) {
  const kind = CALLING_KIND[cls] ?? 'feat';
  const head = CALLING_HEAD[kind];
  const byId = (id: number) => catalogue.find((a) => a.id === id);

  if (kind === 'spells') {
    const max = callings.wizard_picks;
    const n = choice.spells.length;
    const full = n >= max;
    const names = choice.spells.map((id) => byId(id)?.name).filter(Boolean);
    return (
      <Slot slot="calling" label={head.label} open={open} onToggle={onToggle} wants={!full} hint={head.hint}
        value={names.length ? names.join(' · ') : `Choose ${max} spells`} sub={full ? 'Spark is always known too.' : `${max - n} more to learn.`}
        aside={<Badge tone={full ? 'green' : 'amber'} className="fk-forge__count"><span data-testid="forge-spell-count" data-count={n}>{n} of {max}</span></Badge>}>
        <div className="fk-forge__cards fk-forge__cards--spells" role="group" aria-label="Wizard spells">
          {callings.wizard_spells.map((id) => {
            const a = byId(id);
            if (!a) return null;
            const on = choice.spells.includes(id);
            return (
              <Pick key={id} details={<DetailsButton kind="spell" id={id} name={a.name} onOpen={onDetails} />}>
                <Card as="button" interactive column selected={on} dim={!on && full} className="fk-forge__choice fk-forge__spell" aria-pressed={on}
                  data-testid="forge-spell" data-spell-id={id} data-sfx="ui.tab" title={!on && full ? `${max} spells chosen: take one out first` : undefined}
                  onClick={() => { onToggleSpell(id); if (!on && n + 1 === max) onPicked(); }}>
                  <span className="fk-forge__choice-name">{a.name}</span>
                  <span className="fk-forge__spell-meta"><Badge tone={slotTone(a.slot)}>{slotWord(a.slot)}</Badge><span>{rangeWord(a)} · {costWord(a, resource)}</span></span>
                </Card>
              </Pick>
            );
          })}
        </div>
      </Slot>
    );
  }

  const defs = callingsFor(callings, cls);
  const companion = kind === 'companion';
  const cur = defs.find((d) => d.id === choice.pick);
  const curBody = companion && cur ? callings.companions.find((c) => c.calling === cur.id) : undefined;
  const petName = choice.petName.trim();
  return (
    <Slot slot="calling" label={head.label} open={open} onToggle={onToggle} hint={head.hint} wants={!cur || (companion && !petName)}
      art={curBody ? <HeroSprite frame={coatFrame(curBody, choice.petCoat)} scale={2} title={cur!.name} /> : undefined}
      value={!cur ? `Choose a ${head.label.toLowerCase()}` : companion ? (petName && petName !== cur.name ? `${petName} the ${cur.name}` : petName || 'Unnamed') : <>{cur.name}{cur.title && <span className="fk-forge__slot-title">{cur.title}</span>}</>}
      sub={curBody ? companionLine(curBody) : cur?.rules}
      aside={cur && <DetailsButton kind={callingDetailsKind(cur)} id={cur.id} name={cur.name} onOpen={onDetails} />}>
      {/* the companion's name and coat live inside the slot: shut, the slot shows only the pick */}
      {companion && (
        <div className="fk-forge__pet-row">
          <Field label="Companion’s name" htmlFor="fk-forge-pet" hint={`Up to ${callings.pet_name_max} letters. It answers to nothing else.`} className="fk-forge__name fk-forge__pet">
            <Input id="fk-forge-pet" data-testid="forge-pet-name" maxLength={callings.pet_name_max} value={choice.petName} autoComplete="off"
              placeholder="What does it answer to?" aria-invalid={!petName || undefined}
              onChange={(e) => onChange({ ...choice, petName: e.target.value.slice(0, callings.pet_name_max) })} />
          </Field>
          {curBody && curBody.coats.length > 1 && (
            <Field label="Coat" hint="Everyone in the World sees it in this colour." className="fk-forge__pet fk-forge__coat">
              <Segmented label="Companion’s coat" value={choice.petCoat} onChange={(petCoat) => onChange({ ...choice, petCoat })}
                options={curBody.coats.map((c, i) => ({ value: i, title: `${c.name} ${cur!.name.toLowerCase()}`,
                  label: <span className="fk-forge__coat-opt" data-testid="forge-pet-coat" data-coat={i}><span aria-hidden="true"><HeroSprite frame={c.frame} scale={2} /></span>{c.name}</span> }))} />
            </Field>
          )}
        </div>
      )}
      <div className="fk-forge__cards fk-forge__cards--wide" role="group" aria-label={head.label}>
        {defs.map((d) => {
          const on = choice.pick === d.id;
          const body = companion ? callings.companions.find((c) => c.calling === d.id) : undefined;
          const grant = d.grants.map(byId).find(Boolean);
          const pick = () => {
            // a companion still wants its name and coat: the slot stays open for them
            if (!companion) onPicked();
            if (on) return;
            // a companion's name follows the animal until the player types their own
            const prev = defs.find((x) => x.id === choice.pick);
            const keepName = companion && choice.petName.trim() && choice.petName.trim() !== prev?.name;
            onChange({ ...choice, pick: d.id, petName: companion ? (keepName ? choice.petName : d.name) : '', petCoat: 0 });
          };
          return (
            <Pick key={d.id} details={<DetailsButton kind={callingDetailsKind(d)} id={d.id} name={d.name} onOpen={onDetails} />}>
              <Card as="button" interactive column selected={on} className={`fk-forge__choice fk-forge__calling ${companion ? 'fk-forge__calling--beast' : ''}`} aria-pressed={on}
                data-testid="forge-calling" data-calling-id={d.id} data-sfx="ui.forge" onClick={pick}>
                {body ? (
                  <span className="fk-forge__beast">
                    <span className="fk-forge__beast-art"><HeroSprite frame={body.frame} scale={2} title={d.name} /></span>
                    <span className="fk-forge__beast-text">
                      <span className="fk-forge__choice-name">{d.name}</span>
                      <span className="fk-forge__beast-line">{companionLine(body)}</span>
                    </span>
                  </span>
                ) : (
                  <span className="fk-forge__choice-name">{d.name}{grant && <Badge tone={slotTone(grant.slot)}>{slotWord(grant.slot)}</Badge>}</span>
                )}
                {d.title && <span className="fk-forge__choice-sub fk-forge__calling-title">{d.title}</span>}
                <span className="fk-forge__blurb">{d.blurb}</span>
                <span className="fk-forge__calling-rules">{d.rules}</span>
              </Card>
            </Pick>
          );
        })}
      </div>
    </Slot>
  );
}
